import { useEffect, useRef } from 'react';

// 3D Fibonacci point-cloud sphere with slow rotation, breathing, a travelling
// "lit" dot, and radial heartbeat waves. Builds its dots imperatively into the
// given <svg> and returns a stop() that cancels the animation frame.
export function makePointCloud(svg, opts = {}) {
  if (!svg) return null;
  const NS = 'http://www.w3.org/2000/svg';
  const N = opts.N || 130;
  const R = opts.R || 84;
  const PHI = Math.PI * (3 - Math.sqrt(5));

  const dotLayer = document.createElementNS(NS, 'g');
  svg.appendChild(dotLayer);

  const pos = [];
  const dots = [];
  for (let i = 0; i < N; i += 1) {
    const y = 1 - (i / (N - 1)) * 2;
    const rr = Math.sqrt(1 - y * y);
    const t = PHI * i;
    pos.push({ x: Math.cos(t) * rr, y, z: Math.sin(t) * rr });

    const c = document.createElementNS(NS, 'circle');
    c.setAttribute('cx', '0');
    c.setAttribute('cy', '0');
    c.setAttribute('r', '2');
    c.setAttribute('class', 'WSMONAD-sigil-dot');
    dotLayer.appendChild(c);
    dots.push(c);
  }

  const angFromEpicenter = pos.map((p) => Math.acos(Math.max(-1, Math.min(1, p.x))));
  let lit = 0;
  dots[lit].classList.add('WSMONAD-lit');
  let lastSwap = 0;
  let lastBeat = -2000;
  const waves = [];
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const heartbeat = opts.heartbeat !== false;
  const breathe = opts.breathe !== false;

  // slow "assembly" intro: dots converge from a scattered cloud into the sphere
  const assemble = !!opts.assemble && !reduce;
  const assembleMs = opts.assembleMs || 2000;
  const easeOutCubic = (t) => 1 - (1 - t) ** 3;
  const start = assemble
    ? pos.map(() => {
        const rx = Math.random() * 2 - 1;
        const ry = Math.random() * 2 - 1;
        const rz = Math.random() * 2 - 1;
        const m = Math.hypot(rx, ry, rz) || 1;
        const mag = 1.8 + Math.random() * 1.8;
        return { x: (rx / m) * mag, y: (ry / m) * mag, z: (rz / m) * mag };
      })
    : null;
  let t0 = null;
  let raf = 0;

  function render(time) {
    if (t0 === null) t0 = time;
    const ease = assemble ? easeOutCubic(Math.min(1, (time - t0) / assembleMs)) : 1;
    const ry = reduce ? 0.5 : time * 0.00014;
    const rx = reduce ? 0.2 : Math.sin(time * 0.00009) * 0.32;
    const cy = Math.cos(ry);
    const sy = Math.sin(ry);
    const cx = Math.cos(rx);
    const sx = Math.sin(rx);

    const curR = reduce || !breathe ? R : R * (1 + 0.1 * Math.sin(time * 0.0011));

    if (!reduce && time - lastSwap > 1500) {
      dots[lit].classList.remove('WSMONAD-lit');
      lit = (lit + 13) % N;
      dots[lit].classList.add('WSMONAD-lit');
      lastSwap = time;
    }

    if (!reduce && heartbeat && ease >= 1) {
      if (time - lastBeat > 1250) {
        waves.push({ start: time });
        lastBeat = time;
      }
      for (let i = waves.length - 1; i >= 0; i -= 1) {
        if ((time - waves[i].start) / 2200 >= 1) waves.splice(i, 1);
      }
    }

    const proj = new Array(N);
    for (let i = 0; i < N; i += 1) {
      const p = pos[i];
      let bx = p.x;
      let by = p.y;
      let bz = p.z;
      if (ease < 1) {
        const s = start[i];
        bx = s.x + (p.x - s.x) * ease;
        by = s.y + (p.y - s.y) * ease;
        bz = s.z + (p.z - s.z) * ease;
      }
      const x1 = bx * cy + bz * sy;
      const z1 = -bx * sy + bz * cy;
      const y2 = by * cx - z1 * sx;
      const z2 = by * sx + z1 * cx;
      proj[i] = { px: x1 * curR, py: -y2 * curR, depth: (z2 + 1) / 2 };
    }

    for (let i = 0; i < N; i += 1) {
      const p = proj[i];
      let r = 0.9 + p.depth * 2.8;
      let op = 0.16 + p.depth * 0.84;

      if (heartbeat && waves.length) {
        let boost = 0;
        for (let w = 0; w < waves.length; w += 1) {
          const phase = (time - waves[w].start) / 2200;
          if (phase < 0 || phase >= 1) continue;
          const waveAng = phase * Math.PI;
          const dist = Math.abs(angFromEpicenter[i] - waveAng);
          if (dist < 0.32) boost += (1 - dist / 0.32) * (1 - phase);
        }
        if (boost > 0) {
          const front = 0.5 + p.depth * 0.5;
          r += boost * 3.2 * front;
          op = Math.min(1, op + boost * 0.35 * front);
        }
      }

      if (ease < 1) op *= 0.18 + 0.82 * ease;

      const c = dots[i];
      c.setAttribute('cx', p.px.toFixed(2));
      c.setAttribute('cy', p.py.toFixed(2));
      c.setAttribute('r', r.toFixed(2));
      c.setAttribute('opacity', op.toFixed(3));
    }

    if (!reduce) raf = requestAnimationFrame(render);
  }

  if (typeof requestAnimationFrame === 'undefined') return () => {};
  raf = requestAnimationFrame(render);
  return () => {
    if (raf) cancelAnimationFrame(raf);
  };
}

// Hero sphere as a self-contained component (used for the big hero sigil).
export default function MonadSigil({
  svgId,
  viewBox = '-100 -100 200 200',
  N = 130,
  R = 84,
  ariaLabel = 'MONAD sigil',
}) {
  const ref = useRef(null);

  useEffect(() => {
    const svg = ref.current;
    if (!svg) return undefined;
    svg.innerHTML = '';
    const stop = makePointCloud(svg, { N, R });
    return () => {
      if (stop) stop();
    };
  }, [N, R]);

  return (
    <svg id={svgId} ref={ref} viewBox={viewBox} xmlns="http://www.w3.org/2000/svg" aria-label={ariaLabel} />
  );
}
